import type { ConversationListItem, ProjectView } from "@esi/shared";
import { ChevronRight, FolderClosed, FolderOpen, SquarePen } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { local } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { ConversationRowMenu } from "../../conversation/actions.tsx";
import { useConversationPage } from "../../conversation/queries.ts";
import { useProjects } from "../../projects/queries.ts";
import {
  CONVERSATIONS_PATH,
  conversationOf,
  conversationPath,
  Link,
  PROJECTS_PATH,
  projectConversationPath,
  projectHomePath,
  projectOf,
  type Route,
} from "../../router.tsx";
import { useRovingNav } from "./use-roving-nav.ts";

const EXPANDED_KEY = "esi:sidebar-expanded";
/** Projects listed before "Show more", conversations per expanded project, recents. */
export const SIDEBAR_PROJECTS = 8;
export const PROJECT_CONVERSATIONS = 5;
export const RECENTS = 15;

function readExpanded(): Set<string> {
  try {
    const value: unknown = JSON.parse(local.get(EXPANDED_KEY) ?? "[]");
    return new Set(Array.isArray(value) ? value.filter((v) => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

/**
 * The sidebar's Projects and Recents (spec `web-shell` "Projects in the sidebar", "Recents in
 * the sidebar", "Sidebar keyboard navigation"): two `<nav>` lists of links that are one tab
 * stop, walked with the arrow keys. Each project has a link to its home and a disclosure
 * button that expands it in place to its latest conversations (fetched on expand). Expanded
 * projects are remembered in the browser; the current project expands by itself.
 */
export function ConversationTree(props: { route: Route; onNavigate: () => void }) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(readExpanded);
  const currentProject = projectOf(props.route);
  const currentConversation = conversationOf(props.route);
  const toggle = useCallback((projectId: string, open: boolean) => {
    setExpanded((was) => {
      const next = new Set(was);
      if (open) {
        next.add(projectId);
      } else {
        next.delete(projectId);
      }
      local.set(EXPANDED_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);
  // Opening a conversation of a collapsed project expands it, so the entry shows as current.
  useEffect(() => {
    if (currentProject && currentConversation) {
      setExpanded((was) => (was.has(currentProject) ? was : new Set(was).add(currentProject)));
    }
  }, [currentProject, currentConversation]);
  const roving = useRovingNav(container, { onExpand: toggle });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the arrow keys of the entries inside.
    <div
      data-esi-content="conversations"
      ref={container}
      onKeyDown={roving.onKeyDown}
      onFocus={roving.onFocus}
      className="flex flex-col group-data-[collapsible=icon]:hidden"
    >
      <ProjectsSection
        route={props.route}
        expanded={expanded}
        onToggle={toggle}
        onNavigate={props.onNavigate}
      />
      <RecentsSection current={currentConversation} onNavigate={props.onNavigate} />
    </div>
  );
}

function SectionLabel(props: { id: string; children: ReactNode }) {
  return (
    <h2 id={props.id} className="px-2 pt-4 pb-1 text-xs font-medium text-fg-muted">
      {props.children}
    </h2>
  );
}

const ROW =
  "flex h-7 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-[13px] text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring";
const CURRENT = "bg-surface-hover font-medium text-fg";
/** A row control that shows on hover or focus (always on touch screens). */
const REVEAL =
  "opacity-100 [@media(hover:hover)]:opacity-0 group-hover/project:opacity-100 group-focus-within/project:opacity-100";

function ProjectsSection(props: {
  route: Route;
  expanded: ReadonlySet<string>;
  onToggle: (projectId: string, open: boolean) => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const projects = useProjects();
  const list = projects.data ?? [];
  const shown = list.slice(0, SIDEBAR_PROJECTS);
  const currentProject = projectOf(props.route);
  const onProjectPage =
    props.route.name === "projectHome" ||
    props.route.name === "projectSettings" ||
    props.route.name === "wiki" ||
    props.route.name === "wikiNew" ||
    props.route.name === "wikiEdit" ||
    props.route.name === "wikiDraft";
  return (
    <nav aria-labelledby={`${id}-label`} className="px-2">
      <SectionLabel id={`${id}-label`}>{t("nav.projects")}</SectionLabel>
      {projects.isPending ? (
        <div className="flex flex-col gap-1 px-2" aria-busy="true">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : (
        <ul className="flex flex-col gap-px">
          {shown.map((p) => (
            <ProjectEntry
              key={p.id}
              project={p}
              expanded={props.expanded.has(p.id)}
              current={onProjectPage && currentProject === p.id}
              active={currentProject === p.id}
              currentConversation={conversationOf(props.route)}
              onToggle={props.onToggle}
              onNavigate={props.onNavigate}
            />
          ))}
          {/* Always a way to the project list (and its "New project"): "Show more" past eight. */}
          <li>
            <Link
              href={PROJECTS_PATH}
              data-nav-item="projects-more"
              className={cn(ROW, "text-fg-muted")}
              onClick={props.onNavigate}
            >
              {list.length > SIDEBAR_PROJECTS ? t("nav.showMore") : t("nav.allProjects")}
            </Link>
          </li>
        </ul>
      )}
    </nav>
  );
}

function ProjectEntry(props: {
  project: ProjectView;
  expanded: boolean;
  current: boolean;
  /** Its page or one of its conversations is open: its "New conversation" stays shown. */
  active: boolean;
  currentConversation: string | null;
  onToggle: (projectId: string, open: boolean) => void;
  onNavigate: () => void;
}) {
  const { project, expanded } = props;
  const { t } = useTranslation();
  const listId = useId();
  const Folder = expanded ? FolderOpen : FolderClosed;
  // A frozen project starts no conversation (its header offers none either).
  const canStart = project.frozenAt === null;
  return (
    <li className="group/project flex flex-col">
      <div className="flex items-center">
        <Link
          href={projectHomePath(project.id)}
          data-nav-item={project.id}
          data-nav-project=""
          aria-expanded={expanded}
          aria-current={props.current ? "page" : undefined}
          title={project.name}
          onClick={props.onNavigate}
          className={cn(ROW, canStart ? "pr-14" : "pr-7", props.current && CURRENT)}
        >
          <Folder aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
          <span data-esi-content="projects" className="truncate">
            {project.name}
          </span>
        </Link>
        <div className={cn("flex shrink-0 items-center", canStart ? "-ml-13" : "-ml-7")}>
          <button
            type="button"
            tabIndex={-1}
            aria-expanded={expanded}
            aria-controls={listId}
            aria-label={
              expanded
                ? t("nav.collapseProject", { name: project.name })
                : t("nav.expandProject", { name: project.name })
            }
            onClick={() => props.onToggle(project.id, !expanded)}
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-md text-fg-muted outline-none transition-opacity duration-(--duration-fast) hover:bg-surface-selected hover:text-fg",
              // The chevron shows on hover or focus, and always when expanded.
              expanded ? "opacity-100" : REVEAL,
            )}
          >
            <ChevronRight
              aria-hidden="true"
              className={cn(
                "size-3.5 transition-transform duration-(--duration-fast)",
                expanded && "rotate-90",
              )}
              strokeWidth={1.75}
            />
          </button>
          {canStart ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  href={projectConversationPath(project.id, "new")}
                  // The next tab stop after its project while that project holds the group's
                  // tab stop (use-roving-nav); the arrows skip it.
                  data-nav-action={project.id}
                  tabIndex={-1}
                  aria-label={t("nav.newConversationIn", { name: project.name })}
                  onClick={props.onNavigate}
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-md text-fg-muted outline-none transition-opacity duration-(--duration-fast) hover:bg-surface-selected hover:text-fg focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring",
                    // On hover or focus, and always on the open project.
                    props.active ? "opacity-100" : REVEAL,
                  )}
                >
                  <SquarePen aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{t("projectHome.newConversation")}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {expanded ? (
        <ProjectConversations
          id={listId}
          project={project}
          current={props.currentConversation}
          onNavigate={props.onNavigate}
        />
      ) : null}
    </li>
  );
}

function ProjectConversations(props: {
  id: string;
  project: ProjectView;
  current: string | null;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const page = useConversationPage({ projectId: props.project.id, limit: PROJECT_CONVERSATIONS });
  const items = page.data?.items ?? [];
  return (
    <ul id={props.id} className="flex flex-col gap-px">
      {page.isPending ? (
        <li className="py-1 pl-8">
          <Skeleton className="h-4 w-2/3" />
        </li>
      ) : items.length === 0 ? (
        <li className="flex h-7 items-center pl-8 text-[13px] text-fg-muted">
          {page.isError ? page.error.message : t("nav.noConversations")}
        </li>
      ) : (
        <>
          {items.map((c) => (
            <ConversationEntry
              key={c.id}
              conversation={c}
              parent={props.project.id}
              current={props.current === c.id}
              onNavigate={props.onNavigate}
            />
          ))}
          {page.data?.nextCursor ? (
            <li>
              <Link
                href={projectHomePath(props.project.id, "conversations")}
                data-nav-item={`${props.project.id}:more`}
                data-nav-parent={props.project.id}
                className={cn(ROW, "pl-8 text-fg-muted")}
                onClick={props.onNavigate}
              >
                {t("nav.showMore")}
              </Link>
            </li>
          ) : null}
        </>
      )}
    </ul>
  );
}

/** A conversation row: its title, truncated with a tooltip; a shared one says who shared it. */
function ConversationEntry(props: {
  conversation: ConversationListItem;
  /** The project it is listed under (indented), or none (Recents). */
  parent?: string;
  current: boolean;
  onNavigate: () => void;
}) {
  const { conversation: c } = props;
  const { t } = useTranslation();
  const title = c.title ?? t("conversation.untitled");
  const name = c.sharedBy
    ? t("nav.sharedEntry", { title, name: c.sharedBy.name ?? t("share.someone") })
    : title;
  const tooltip = c.project && !props.parent ? `${name} · ${c.project.name}` : name;
  return (
    <li className="group/entry relative flex items-center">
      <Link
        href={conversationPath(c)}
        data-nav-item={props.parent ? `${props.parent}:${c.id}` : c.id}
        data-nav-parent={props.parent}
        aria-current={props.current ? "page" : undefined}
        aria-label={name === title ? undefined : name}
        title={tooltip}
        onClick={props.onNavigate}
        className={cn(ROW, "pr-7", props.parent && "pl-8", props.current && CURRENT)}
      >
        <span className="truncate">{title}</span>
      </Link>
      <ConversationRowMenu
        conversation={c}
        tabIndex={-1}
        className="absolute right-0.5 [@media(hover:hover)]:opacity-0 group-hover/entry:opacity-100 group-focus-within/entry:opacity-100 data-[state=open]:opacity-100"
      />
    </li>
  );
}

function RecentsSection(props: { current: string | null; onNavigate: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const page = useConversationPage({ limit: RECENTS });
  const items = page.data?.items ?? [];
  return (
    <nav aria-labelledby={`${id}-label`} className="px-2">
      <SectionLabel id={`${id}-label`}>{t("nav.recents")}</SectionLabel>
      {page.isPending ? (
        <div className="flex flex-col gap-1 px-2" aria-busy="true">
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-3/5" />
        </div>
      ) : page.isError ? (
        <p className="px-2 text-[13px] text-fg-muted">{page.error.message}</p>
      ) : (
        <ul className="flex flex-col gap-px">
          {items.length === 0 ? (
            <li className="flex h-7 items-center px-2 text-[13px] text-fg-muted">
              {t("nav.noConversations")}
            </li>
          ) : null}
          {items.map((c) => (
            <ConversationEntry
              key={c.id}
              conversation={c}
              current={props.current === c.id}
              onNavigate={props.onNavigate}
            />
          ))}
          <li>
            <Link
              href={CONVERSATIONS_PATH}
              data-nav-item="recents-all"
              className={cn(ROW, "text-fg-muted")}
              onClick={props.onNavigate}
            >
              {t("nav.showAll")}
            </Link>
          </li>
        </ul>
      )}
    </nav>
  );
}
